import React, { useMemo } from 'react'

import { Checkbox, FormControlLabel, Grid, TextareaAutosize, Typography, Button } from '@barracuda-internal/bds-core-v2'
import { aliasesSpaceContainerSpacious } from '@barracuda-internal/bds-core-v2/dist/styles/bdstokens/build/ts/all_cudaLight_default'
import { HelpOutline } from '@barracuda-internal/bds-core-v2/dist/Icons/Core'
import { useFormatMessage } from 'lib/localization'
import { AvailableSettings } from 'types/Settings'

import { MAX_CHAR, useTaglineLogic } from 'components/pages/outboundSettings/tagline/useTaglineLogic'
import { PageContainerV2 } from 'components/libs/layout/pages/PageContainerV2'
import { useIsReadonly } from 'lib/hooks/useIsReadonly'
import NoDomainPolicyAccessAlert from 'components/libs/layout/pages/NoDomainPolicyAccessAlert'

const BASE_I18N_KEY = 'ess.outbound_settings.tagline'
const BUTTON_I18N_KEY = 'ess.button'

const Tagline: React.FC = () => {
  const isReadonly = useIsReadonly()
  const formatMessage = useFormatMessage(BASE_I18N_KEY)
  const buttonFormatMessage = useFormatMessage(BUTTON_I18N_KEY)

  const [state, eventHandlers] = useTaglineLogic()

  const mastheadRightContent = useMemo(
    () => (
      <>
        <Button
          onClick={eventHandlers.onCancelConfirm}
          disabled={isReadonly || !state.isDirtyForm}
          bdsType="interactiveSubtle"
        >
          {buttonFormatMessage('cancel')}
        </Button>
        <Button
          onClick={eventHandlers.onSave}
          disabled={isReadonly || !state.isDirtyForm}
          bdsType="interactiveEmphasis"
        >
          {buttonFormatMessage('save')}
        </Button>
      </>
    ),
    [eventHandlers.onCancelConfirm, eventHandlers.onSave, isReadonly, state.isDirtyForm, buttonFormatMessage]
  )

  return useMemo(
    () => (
      <PageContainerV2
        key={state.key}
        dirtyCheck
        isDirtyForm={state.isDirtyForm}
        includeSettingsLogic
        includeDomainConflict
        masthead={{
          pageTitle: formatMessage('title'),
          helperIcon: {
            icon: <HelpOutline />,
            linkContent: {
              href: '/admin/support/help/outbound-tagline',
              rel: 'noopener noreferrer',
              target: '_blank'
            }
          },
          rightContent: mastheadRightContent
        }}
      >
        {state.hasNoDomainPolicyAccess && <NoDomainPolicyAccessAlert />}
        {!state.hasNoDomainPolicyAccess && (
          <Grid container direction="column" spacing={aliasesSpaceContainerSpacious}>
            <Grid item>
              <Grid container direction="row" spacing={aliasesSpaceContainerSpacious}>
                <Grid item xs={12} sm={3} sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                  <Typography variant="h4">{formatMessage('append_label')}</Typography>
                </Grid>
                <Grid item xs={12} sm={9}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={state.form.outbound_tagline_enabled === '1'}
                        onChange={eventHandlers.onCheckboxChange}
                        name={AvailableSettings.OUTBOUND_TAGLINE_ENABLED}
                        color="primary"
                        size="small"
                        data-testid="checkbox"
                        disabled={isReadonly}
                      />
                    }
                    label={formatMessage('append_description')}
                    variant="toggle"
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <Grid container direction="row" spacing={aliasesSpaceContainerSpacious}>
                <Grid item xs={12} sm={3} sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                  <Typography variant="h4">{formatMessage('tagline_label')}</Typography>
                </Grid>
                <Grid item xs={12} sm={9}>
                  <TextareaAutosize
                    minRows={16}
                    maxRows={16}
                    value={state.form.outbound_tagline}
                    onChange={eventHandlers.onTextChange}
                    data-testid="textarea"
                    maxLength={MAX_CHAR}
                    disabled={isReadonly}
                  />
                  <Typography>{formatMessage('characters_left', { charactersLeft: state.charactersLeft })}</Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}
      </PageContainerV2>
    ),
    [
      state.key,
      state.isDirtyForm,
      state.form.outbound_tagline_enabled,
      state.form.outbound_tagline,
      state.charactersLeft,
      formatMessage,
      eventHandlers.onCheckboxChange,
      eventHandlers.onTextChange,
      isReadonly,
      mastheadRightContent,
      state.hasNoDomainPolicyAccess
    ]
  )
}

export default Tagline
